import IndexedDBHelper from './IndexedDBHelper.js';
import appSettingStore from '../components/AppSettingStore.js';
import attributeItemStore from '../components/AttributeItemStore.js';
import eventItemStore from '../components/EventItemStore.js';
import scriptItemStore from '../components/ScriptItemStore.js';
import serviceworkerItemStore from '../components/ServiceWorkerItemStore.js';
import templateJsonStore from '../components/TemplateJsonStore.js';
import toolBoxItemStore from '../components/ToolBoxItemStore.js';
import testItemStore from '../components/TestItemStore.js';

import { loadAppSettingData } from '../components/AppSettingSlice.js';
import { loadAttributeItemData } from '../components/AttributeItemSlice.js';
import { loadEventItemData } from '../components/EventItemSlice.js';
import { loadScriptData } from '../components/ScriptItemSlice.js';
import { loadServiceWorkerItemData } from '../components/ServiceWorkerItemSlice.js';
import { loadTemplateJson } from '../components/TemplateJsonSlice.js';
import { loadToolBoxCustomData } from '../components/ToolBoxItemSlice.js';
import { loadTestData } from '../components/TestItemSlice.js';
import templateSelectedRootStore from '../components/TemplateSelectedRootStore.js';
import { resetRoot } from '../components/TemplateSelectedRootSlice.js';
import templateSelectedItemStore from '../components/TemplateSelectedItemStore.js';
import { deselectItem } from '../components/TemplateSelectedItemSlice.js';

class SnapshotController {
  constructor() {
    this.identKey = null;
    this.appKey = null;
    this.version = null;

    // URLからキーを取得
    const urlParams = new URLSearchParams(window.location.search);
    this.identKey = urlParams.get('identKey');
    this.appKey = urlParams.get('appKey');
    this.version = urlParams.get('version');
  }

  /**
   * 現在のすべてのストアの状態を収集する
   * @returns {object} すべてのストアの状態を含むオブジェクト
   */
  _gatherAllState() {
    return {
      appSetting: appSettingStore.getState().appSetting,
      attributeItems: attributeItemStore.getState().attributeItems.items,
      eventItems: eventItemStore.getState().eventItems.items,
      scriptItems: scriptItemStore.getState().scriptItems,
      serviceworkerItems: serviceworkerItemStore.getState().serviceworkerItems.items,
      templateJson: templateJsonStore.getState().templateJson,
      toolBoxItems: toolBoxItemStore.getState().toolboxItems.customItems,
      testItems: testItemStore.getState().testItems,
    };
  }

  /**
   * スナップショットデータで全てのストアを復元する
   * @param {object} snapshotData - 復元するデータ
   */
  _restoreAllState(snapshotData) {
    if (!snapshotData || !snapshotData.state) return;

    const state = snapshotData.state;

    // 各ストアにロードアクションをディスパッチ
    appSettingStore.dispatch(loadAppSettingData(state.appSetting));
    attributeItemStore.dispatch(loadAttributeItemData(state.attributeItems));
    eventItemStore.dispatch(loadEventItemData(state.eventItems));
    scriptItemStore.dispatch(loadScriptData(state.scriptItems));
    serviceworkerItemStore.dispatch(loadServiceWorkerItemData(state.serviceworkerItems));
    templateJsonStore.dispatch(loadTemplateJson(state.templateJson));
    toolBoxItemStore.dispatch(loadToolBoxCustomData(state.toolBoxItems));

    // テストデータの復元
    if (state.testItems) {
      testItemStore.dispatch(loadTestData(state.testItems));
    }

    templateSelectedRootStore.dispatch(resetRoot());
    templateSelectedItemStore.dispatch(deselectItem());
  }

  /**
   * 特定の名前を持つスナップショットが複数存在する場合、最新の1つを残して他をすべて削除します。
   * @param {string} name - クリーンアップ対象のスナップショットの名前
   */
  async cleanupDuplicateSnapshots(name) {
    // 現在のプロジェクトの全てのスナップショットを取得します。
    // getSnapshotsForCurrentProjectはタイムスタンプの降順（新しいものが先頭）でソート済みです。
    const allSnapshots = await this.getSnapshotsForCurrentProject();

    // 指定された名前を持つスナップショットのみを抽出します。
    const duplicates = allSnapshots.filter(s => s.name === name);

    // 同じ名前のスナップショットが2つ以上存在する場合（つまり、クリーンアップが必要な場合）
    if (duplicates.length > 1) {
      // slice(1) を使って、配列の最初の要素（最新のスナップショット）を除く全てのスナップショットを取得します。
      const snapshotsToDelete = duplicates.slice(1);

      // 削除対象のスナップショットを並行して削除します。
      const deletePromises = snapshotsToDelete.map(s => this.deleteSnapshot(s.id));
      await Promise.all(deletePromises);
    }
  }

  /**
   * 特定のスナップショット名を除くすべてのスナップショットをクリアする新しいメソッド
   * 現在のプロジェクトのスナップショットのうち、指定された名前のスナップショットを除いてすべて削除します。
   * @param {string[]} excludeNames - 削除対象外とするスナップショット名の配列
   */
  async clearAllExcept(excludeNames = []) {
    const allSnapshots = await this.getSnapshotsForCurrentProject();
    const snapshotsToDelete = allSnapshots.filter(s => !excludeNames.includes(s.name));

    const deletePromises = snapshotsToDelete.map(s => this.deleteSnapshot(s.id));
    await Promise.all(deletePromises);
  }

  /**
   * 現在の状態でスナップショットを作成し、DBに保存
   * @param {string} name - スナップショットの名前
   * @param {string} description - スナップショットの説明
   */
  async createSnapshot(name, description) {
    const snapshot = {
      identKey: this.identKey,
      appKey: this.appKey,
      version: this.version,
      name: name,
      description: description,
      timestamp: new Date().toISOString(),
      state: this._gatherAllState(),
    };
    await IndexedDBHelper.saveSnapshot(snapshot);
  }

  /**
  * 自動保存用のメソッド。
  * 特定の名前のスナップショットを新規作成し、同名でタイムスタンプが古いものを削除して1つだけ保持します。
  * @param {string} name - スナップショットの名前
  * @param {string} description - スナップショットの説明
  */
  async createAutoSnapshot(name, description) {
    // 最初に新しいスナップショットを通常通り作成します。
    await this.createSnapshot(name, description);

    // 次に、同じ名前の古いスナップショットをクリーンアップします。
    await this.cleanupDuplicateSnapshots(name);
  }

  /**
   * 現在のプロジェクトのスナップショットをすべて取得
   * @returns {Promise<Array<object>>}
   */
  async getSnapshotsForCurrentProject() {
    return await IndexedDBHelper.getSnapshots(this.identKey, this.appKey, this.version);
  }

  /**
   * IDで指定されたスナップショットを復元する
   * @param {number} id - 復元するスナップショットのID
   */
  async restoreSnapshot(id) {
    const snapshots = await this.getSnapshotsForCurrentProject();
    const snapshotToRestore = snapshots.find(s => s.id === id);
    if (snapshotToRestore) {
      this._restoreAllState(snapshotToRestore);
    } else {
      throw new Error("指定されたスナップショットが見つかりません。");
    }
  }

  /**
   * IDで指定されたスナップショットを削除する
   * @param {number} id - 削除するスナップショットのID
   */
  async deleteSnapshot(id) {
    await IndexedDBHelper.deleteSnapshot(id);
  }

  /**
   * 現在のプロジェクトのスナップショットをすべて削除
   */
  async clearSnapshotsForCurrentProject() {
    await IndexedDBHelper.clearSnapshots(this.identKey, this.appKey, this.version);
  }

  /**
   * DBに保存されているスナップショットのうち、指定された日数より古いものを削除します。
   * @param {number} days - 削除対象とする基準日数（例: 30日）
   */
  async cleanupOldSnapshots(days) {
    await IndexedDBHelper.deleteOldSnapshots(days);
    console.log(`${days}日以上前の古いスナップショットをクリーンアップしました。`);
  }

  /**
   * 現在の状態を「.pwappy」ファイルとして書き出す
   */
  async exportProjectToFile() {
    const allState = this._gatherAllState();

    // .env から現在のエディタのバージョンを取得
    const currentEditorVersion = process.env.EDITOR_VERSION || "1.0";

    const exportData = {
      type: "pwappy-project-backup",
      metadata: {
        projectName: allState.appSetting.name,
        appKey: this.appKey,
        appVersion: this.version,
        editorVersion: currentEditorVersion,
        timestamp: new Date().toISOString()
      },
      state: allState
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateStr = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.download = `${this.appKey}_v${currentEditorVersion}_${dateStr}.pwappy`;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * 外部ファイルからデータを読み込み、現在の状態を上書きする
   * 実行前に必ず安全のためのスナップショットを作成する
   * 
   * @param {File} file - ブラウザのinput要素から取得したファイルオブジェクト
   * @returns {Promise<object|null>} インポートされたデータ、またはバージョン警告でキャンセルされた場合はnull
   */
  async importProjectFromFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = JSON.parse(e.target.result);

          // ファイル形式とデータの存在チェック
          if (data.type !== "pwappy-project-backup" || !data.state) {
            throw new Error("有効なバックアップファイルではありません。");
          }

          // --- バージョンチェック ---
          // .env から現在のエディタのバージョンを取得
          const fileVersion = data.metadata?.editorVersion;
          const currentEditorVersion = process.env.EDITOR_VERSION || "1.0";

          if (fileVersion !== currentEditorVersion) {
            const proceed = confirm(
              `警告: データのバージョンが異なります。\n` +
              `ファイル: v${fileVersion} / 現在のエディタ: v${currentEditorVersion}\n\n` +
              `構造が変わっている場合、正しく読み込めない可能性があります。続行しますか？`
            );

            if (!proceed) {
              // キャンセルされた場合は null を返して Promise を解決する
              // これにより、呼び出し側で「エラーではないが中断」を判断できる
              resolve(null);
              return;
            }
          }

          // --- 安全策: インポート直前の状態をスナップショットとして保存 ---
          // restoreAllState でストアが上書きされる前に現在の状態をDBに退避する
          await this.createSnapshot(
            "自動保存 - インポート実行前",
            `ファイル「${file.name}」(データVer: v${fileVersion}) のインポート直前の状態です。`
          );

          // 全てのReduxストアの状態をファイルの内容で復元
          this._restoreAllState(data);

          // 成功したデータを返して Promise を解決
          resolve(data);
        } catch (err) {
          // JSONのパースエラーや、ファイルの構造自体に問題がある場合
          reject(err);
        }
      };

      reader.onerror = () => {
        reject(new Error("ファイルの読み込み中にネットワークまたはシステムエラーが発生しました。"));
      };

      // ファイルをテキストとして読み込み開始
      reader.readAsText(file);
    });
  }
}

export default new SnapshotController();